import {
  BadgeEuro,
  BanknoteArrowDown,
  CheckCircle,
  CircleAlert,
  FilePlus2,
  Handshake,
  PencilLine,
  Trash2,
} from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogPanel, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

import { useDeleteMonetaryDemand, useUpsertMonetaryDemand } from "../api/use-monetary-demand-mutations";
import { CREATABLE_MONETARY_DEMAND_TYPES, type MonetaryDemandType } from "../lib/monetary-demand-types";
import { type AmountWithConversions, formatAmount } from "../lib/money";
import type { ClaimMonetaryDemand } from "./monetary-demands-list";

function toAmountInput(amount: { amount: number } | null | undefined) {
  return amount ? (amount.amount / 100).toFixed(2) : "";
}

function parseAmountInput(value: string): number | undefined {
  if (!value.trim()) {
    return undefined;
  }
  const parsed = Number.parseFloat(value.replace(",", "."));
  return Number.isNaN(parsed) ? undefined : Math.round(parsed * 100);
}

interface UpsertMonetaryDemandDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  claimId: string;
  existingDemand?: ClaimMonetaryDemand | null;
}

/** Port of UpsertClaimMonetaryDemand.vue (create/edit/delete a monetary demand), shown inside MonetaryDemandsList's dialog. */
export function UpsertMonetaryDemandDialog({
  open,
  onOpenChange,
  claimId,
  existingDemand,
}: UpsertMonetaryDemandDialogProps) {
  const isUpdate = Boolean(existingDemand);
  const isGenerated = existingDemand?.monetaryDemandType.isGenerated === true;

  const upsertMonetaryDemand = useUpsertMonetaryDemand(claimId);
  const deleteMonetaryDemand = useDeleteMonetaryDemand(claimId);
  const isLoading = upsertMonetaryDemand.isPending || deleteMonetaryDemand.isPending;

  const [monetaryDemandType, setMonetaryDemandType] = useState<MonetaryDemandType>("ARTICLE_700_CPC");
  const [requestedAmount, setRequestedAmount] = useState("");
  const [receivedAmount, setReceivedAmount] = useState("");
  const [adversaryAmount, setAdversaryAmount] = useState("");
  const [agreedAmount, setAgreedAmount] = useState("");
  const [requestReason, setRequestReason] = useState("");

  // Re-seed the form each time the dialog opens (mirrors Vue's defaultRequest).
  useEffect(() => {
    if (!open) {
      return;
    }
    setMonetaryDemandType(existingDemand?.monetaryDemandType.type ?? "ARTICLE_700_CPC");
    setRequestedAmount(toAmountInput(existingDemand?.requestedAmount));
    setReceivedAmount(toAmountInput(existingDemand?.receivedAmount));
    setAdversaryAmount(toAmountInput(existingDemand?.adversaryAmount));
    setAgreedAmount(toAmountInput(existingDemand?.agreedAmount));
    setRequestReason(existingDemand?.requestReason ?? "");
  }, [open, existingDemand]);

  async function submit() {
    try {
      const { success } = await upsertMonetaryDemand.mutateAsync({
        requestReason: requestReason || null,
        monetaryDemandType,
        requestedAmount: parseAmountInput(requestedAmount) ?? 0,
        receivedAmount: parseAmountInput(receivedAmount) ?? null,
        adversaryProposedAmount: parseAmountInput(adversaryAmount) ?? null,
        agreedAmount: parseAmountInput(agreedAmount) ?? null,
      });
      if (success) {
        onOpenChange(false);
      }
    } catch {
      // Error toasts handled by notify.promise.
    }
  }

  async function removeDemand() {
    if (!existingDemand) {
      return;
    }
    try {
      const { success } = await deleteMonetaryDemand.mutateAsync(existingDemand.monetaryDemandType.type);
      if (success) {
        onOpenChange(false);
      }
    } catch {
      // Error toasts handled by notify.promise.
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {isUpdate ? "Modifier une demande monétaire" : "Ajouter une demande supplémentaire"}
          </DialogTitle>
        </DialogHeader>
        <DialogPanel>
          {isGenerated && existingDemand ? (
            <div className="mb-4 rounded-md bg-green-50 p-4">
              <div className="flex">
                <CheckCircle className="size-5 shrink-0 text-green-400" aria-hidden="true" />
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-green-800">
                    Le montant de la demande est calculé en fonction des dépenses
                  </h3>
                  <div className="mt-2 text-sm text-green-700">
                    <p>
                      Total demandé :{" "}
                      {toAmountInput(existingDemand.requestedAmount)
                        ? formatAmount(existingDemand.requestedAmount as AmountWithConversions)
                        : "0€"}
                    </p>
                    <p>
                      Total obtenu :{" "}
                      {existingDemand.receivedAmount
                        ? formatAmount(existingDemand.receivedAmount as AmountWithConversions)
                        : "0€"}{" "}
                      <span className="text-green-600">(somme des remboursements de chaque dépense)</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          <form className="text-left" onSubmit={(event) => event.preventDefault()}>
            <div className="mb-6 space-y-4">
              {!isGenerated ? (
                <div>
                  <Label className="block text-sm font-medium text-gray-900">Type de demande</Label>
                  <Select
                    disabled={isUpdate}
                    value={monetaryDemandType}
                    onValueChange={(value) => {
                      if (typeof value === "string") {
                        setMonetaryDemandType(value as MonetaryDemandType);
                      }
                    }}
                  >
                    <SelectTrigger className="mt-2 w-full">
                      <SelectValue>
                        {
                          CREATABLE_MONETARY_DEMAND_TYPES.find(
                            (option) => option.value === monetaryDemandType,
                          )?.label
                        }
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {CREATABLE_MONETARY_DEMAND_TYPES.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : null}

              {!isGenerated ? (
                <div>
                  <Label
                    htmlFor="requested-amount"
                    className="flex items-center gap-1.5 text-sm font-medium text-gray-900"
                  >
                    <BadgeEuro className="size-4 text-gray-400" />
                    Montant demandé
                  </Label>
                  <Input
                    id="requested-amount"
                    className="mt-2"
                    inputMode="decimal"
                    placeholder="0.00"
                    value={requestedAmount}
                    onChange={(event) => setRequestedAmount(event.currentTarget.value)}
                  />
                </div>
              ) : null}

              {!isGenerated ? (
                <div>
                  <Label
                    htmlFor="received-amount"
                    className="flex items-center gap-1.5 text-sm font-medium text-gray-900"
                  >
                    <BanknoteArrowDown className="size-4 text-gray-400" />
                    Montant obtenu
                  </Label>
                  <Input
                    id="received-amount"
                    className="mt-2"
                    inputMode="decimal"
                    placeholder="0.00"
                    value={receivedAmount}
                    onChange={(event) => setReceivedAmount(event.currentTarget.value)}
                  />
                </div>
              ) : null}

              <div>
                <Label
                  htmlFor="adversary-amount"
                  className="flex items-center gap-1.5 text-sm font-medium text-gray-900"
                >
                  <CircleAlert className="size-4 text-gray-400" />
                  Montant proposé par l'adversaire
                </Label>
                <Input
                  id="adversary-amount"
                  className="mt-2"
                  inputMode="decimal"
                  placeholder="0.00"
                  value={adversaryAmount}
                  onChange={(event) => setAdversaryAmount(event.currentTarget.value)}
                />
              </div>

              <div>
                <Label
                  htmlFor="agreed-amount"
                  className="flex items-center gap-1.5 text-sm font-medium text-gray-900"
                >
                  <Handshake className="size-4 text-gray-400" />
                  Montant accordé
                </Label>
                <Input
                  id="agreed-amount"
                  className="mt-2"
                  inputMode="decimal"
                  placeholder="0.00"
                  value={agreedAmount}
                  onChange={(event) => setAgreedAmount(event.currentTarget.value)}
                />
              </div>

              <div>
                <Label htmlFor="request-reason" className="block text-sm font-medium text-gray-900">
                  {isUpdate ? "Raison de la modification" : "Raison de la création"}
                </Label>
                <Input
                  id="request-reason"
                  className="mt-2"
                  placeholder={
                    isUpdate
                      ? "Ex: Correction du montant"
                      : "Ex: Absence de réponse de la compagnie depuis 2 mois"
                  }
                  value={requestReason}
                  onChange={(event) => setRequestReason(event.currentTarget.value)}
                />
              </div>
            </div>

            <div className="flex flex-row-reverse items-center gap-6">
              <Button type="button" disabled={isLoading} onClick={() => void submit()}>
                {isUpdate ? <PencilLine data-icon="inline-start" /> : <FilePlus2 data-icon="inline-start" />}
                {isUpdate ? "Modifier la demande" : "Ajouter la demande"}
              </Button>
              {isUpdate && !isGenerated ? (
                <Button
                  type="button"
                  variant="outline"
                  disabled={isLoading}
                  onClick={() => void removeDemand()}
                >
                  <Trash2 data-icon="inline-start" />
                  Supprimer la demande
                </Button>
              ) : null}
            </div>
          </form>
        </DialogPanel>
      </DialogContent>
    </Dialog>
  );
}
